import React, { createContext, useContext, useEffect, useState, ReactNode } from 'react';
import { 
  User as FirebaseUser,
  signInWithEmailAndPassword,
  createUserWithEmailAndPassword,
  signInWithPopup,
  signOut as firebaseSignOut,
  onAuthStateChanged,
  updateProfile
} from 'firebase/auth';
import { auth, googleProvider } from '../config/firebase';
import { GoogleDriveService } from '../services/GoogleDriveService';
import { getBackendConfig } from '../config/backend';

export interface User {
  id: number;
  email: string;
  displayName: string | null;
  authProvider: 'email' | 'google';
  tier: 'free' | 'premium';
  createdAt: Date;
  lastLogin: Date | null;
}

interface AuthContextType {
  user: User | null;
  firebaseUser: FirebaseUser | null;
  loading: boolean;
  error: string | null;
  signUp: (email: string, password: string, displayName: string) => Promise<void>;
  signIn: (email: string, password: string) => Promise<void>;
  signInWithGoogle: () => Promise<void>;
  signOut: () => Promise<void>;
  sendPasswordReset: (email: string) => Promise<void>;
  confirmPasswordReset: (code: string, newPassword: string) => Promise<void>;
  clearError: () => void;
  refreshUser: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

interface AuthProviderProps {
  children: ReactNode;
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [firebaseUser, setFirebaseUser] = useState<FirebaseUser | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Get API base URL from config
  const getApiUrl = () => {
    return getBackendConfig().apiBaseUrl;
  };

  // Verify Firebase token with backend
  const verifyTokenWithBackend = async (idToken: string): Promise<User | null> => {
    try {
      const response = await fetch(`${getApiUrl()}/auth/verify-token`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ idToken }),
      });

      if (!response.ok) {
        throw new Error('Token verification failed');
      }

      const data = await response.json();
      console.log('📦 Backend response:', data);
      console.log('👤 User data from backend:', data.user);
      return data.user;
    } catch (error) {
      console.error('❌ Token verification failed:', error);
      console.log('⚠️ Backend unavailable, using Firebase-only authentication');
      
      // Return a basic user object if backend is unavailable
      return {
        id: 1, // Temporary ID
        email: 'user@example.com', // Will be overridden by Firebase user
        displayName: null,
        authProvider: 'email' as const,
        tier: 'free' as const,
        createdAt: new Date(),
        lastLogin: null
      };
    }
  };

  // Listen to Firebase auth state changes
  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (firebaseUser) => {
      setLoading(true);
      setError(null);

      if (firebaseUser) {
        try {
          // Get Firebase ID token
          const idToken = await firebaseUser.getIdToken();
          
          // Verify token with backend and get user info
          const userData = await verifyTokenWithBackend(idToken);
          
          if (userData) {
            setFirebaseUser(firebaseUser);
            // Use Firebase user's email if backend is unavailable
            // Also use Firebase user's displayName if available (it's more up-to-date)
            const finalUserData = {
              ...userData,
              email: userData.email === 'user@example.com' ? firebaseUser.email! : userData.email,
              displayName: firebaseUser.displayName || userData.displayName || null
            };
            console.log('🔍 Final user data being set:', finalUserData);
            console.log('   - Email:', finalUserData.email);
            console.log('   - Display Name:', finalUserData.displayName);
            console.log('   - Tier:', finalUserData.tier);
            console.log('   - Firebase displayName:', firebaseUser.displayName);
            setUser(finalUserData);
            console.log('✅ User authenticated:', finalUserData.email);
          } else {
            // Token verification failed, sign out
            await firebaseSignOut(auth);
            setFirebaseUser(null);
            setUser(null);
          }
        } catch (error) {
          console.error('❌ Auth state change error:', error);
          setFirebaseUser(null);
          setUser(null);
          setError('Authentication failed');
        }
      } else {
        setFirebaseUser(null);
        setUser(null);
        console.log('👤 User signed out');
      }

      setLoading(false);
    });

    return unsubscribe;
  }, []);

  const signUp = async (email: string, password: string, displayName: string): Promise<void> => {
    try {
      setError(null);
      setLoading(true);

      // Create user with Firebase
      const userCredential = await createUserWithEmailAndPassword(auth, email, password);
      
      // Update display name using the updateProfile function
      if (displayName) {
        await updateProfile(userCredential.user, {
          displayName: displayName
        });
      }

      const firebaseUser = userCredential.user;
      
      // Get ID token and verify with backend
      const idToken = await firebaseUser.getIdToken();
      const userData = await verifyTokenWithBackend(idToken);
      
      if (userData) {
        // Set firebaseUser state
        setFirebaseUser(firebaseUser);
        
        // Use the same merging logic as onAuthStateChanged
        const finalUserData = {
          ...userData,
          email: userData.email === 'user@example.com' ? firebaseUser.email! : userData.email,
          displayName: firebaseUser.displayName || userData.displayName || null
        };
        
        setUser(finalUserData);
        console.log('✅ User registered successfully:', finalUserData.email);
      } else {
        throw new Error('Failed to sync user with backend');
      }
    } catch (error: any) {
      console.error('❌ Sign up error:', error);
      
      // Provide more helpful error messages
      let errorMessage = 'Failed to create account';
      if (error.code === 'auth/email-already-in-use') {
        errorMessage = 'An account with this email already exists. Please sign in instead.';
      } else if (error.code === 'auth/invalid-email') {
        errorMessage = 'Invalid email address. Please check your email format.';
      } else if (error.code === 'auth/weak-password') {
        errorMessage = 'Password is too weak. Please use a stronger password (at least 6 characters).';
      } else if (error.message) {
        errorMessage = error.message;
      }
      
      setError(errorMessage);
      throw error;
    } finally {
      setLoading(false);
    }
  };

  const signIn = async (email: string, password: string): Promise<void> => {
    try {
      setError(null);
      setLoading(true);

      // Sign in with Firebase
      const userCredential = await signInWithEmailAndPassword(auth, email, password);
      const firebaseUser = userCredential.user;
      
      // Get ID token and verify with backend
      const idToken = await firebaseUser.getIdToken();
      const userData = await verifyTokenWithBackend(idToken);
      
      if (userData) {
        // Set firebaseUser state
        setFirebaseUser(firebaseUser);
        
        // Use the same merging logic as onAuthStateChanged
        const finalUserData = {
          ...userData,
          email: userData.email === 'user@example.com' ? firebaseUser.email! : userData.email,
          displayName: firebaseUser.displayName || userData.displayName || null
        };
        
        setUser(finalUserData);
        console.log('✅ User signed in:', finalUserData.email);
      } else {
        throw new Error('Failed to sync user with backend');
      }
    } catch (error: any) {
      console.error('❌ Sign in error:', error);
      
      // Provide more helpful error messages
      let errorMessage = 'Failed to sign in';
      if (error.code === 'auth/invalid-credential' || error.code === 'auth/wrong-password' || error.code === 'auth/user-not-found') {
        errorMessage = 'Invalid email or password. Please check your credentials or sign up if you don\'t have an account.';
      } else if (error.code === 'auth/invalid-email') {
        errorMessage = 'Invalid email address. Please check your email format.';
      } else if (error.code === 'auth/too-many-requests') {
        errorMessage = 'Too many failed login attempts. Please try again later.';
      } else if (error.message) {
        errorMessage = error.message;
      }
      
      setError(errorMessage);
      throw error;
    } finally {
      setLoading(false);
    }
  };

  const signInWithGoogle = async (): Promise<void> => {
    try {
      setError(null);
      setLoading(true);

      // Sign in with Google
      const result = await signInWithPopup(auth, googleProvider);
      
      // Check if popup was closed before proceeding with backend verification
      // This helps detect closure faster
      if (!result || !result.user) {
        throw new Error('Popup was closed');
      }
      
      const firebaseUser = result.user;
      
      // Get ID token and verify with backend
      const idToken = await firebaseUser.getIdToken();
      const userData = await verifyTokenWithBackend(idToken);
      
      if (userData) {
        // Set firebaseUser state
        setFirebaseUser(firebaseUser);
        
        // Use the same merging logic as onAuthStateChanged
        const finalUserData = {
          ...userData,
          email: userData.email === 'user@example.com' ? firebaseUser.email! : userData.email,
          displayName: firebaseUser.displayName || userData.displayName || null
        };
        
        setUser(finalUserData);
        console.log('✅ User signed in with Google:', finalUserData.email);
      } else {
        throw new Error('Failed to sync user with backend');
      }
    } catch (error: any) {
      console.error('❌ Google sign in error:', error);
      // Don't set error for popup closure - let the component handle it
      // Throw immediately for popup closure errors to allow fast reset
      if (error.code === 'auth/popup-closed-by-user' || 
          error.code === 'auth/cancelled-popup-request' ||
          error.code === 'auth/popup-blocked') {
        setLoading(false);
        throw error; // Throw immediately without backend delay
      }
      
      if (error.code !== 'auth/popup-closed-by-user' && 
          error.code !== 'auth/cancelled-popup-request' &&
          error.code !== 'auth/popup-blocked') {
        setError(error.message || 'Failed to sign in with Google');
      }
      throw error;
    } finally {
      setLoading(false);
    }
  };

  const signOut = async (): Promise<void> => {
    try {
      setError(null);
      // Clear Google Drive token before signing out
      GoogleDriveService.clearStoredToken();
      await firebaseSignOut(auth);
      setUser(null);
      setFirebaseUser(null);
      console.log('✅ User signed out');
    } catch (error: any) {
      console.error('❌ Sign out error:', error);
      setError(error.message || 'Failed to sign out');
      throw error;
    }
  };

  const sendPasswordReset = async (email: string): Promise<void> => {
    try {
      setError(null);
      
      // Use backend password reset endpoint
      const response = await fetch(`${getApiUrl()}/auth/forgot-password`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ email }),
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || 'Failed to send password reset email');
      }

      const data = await response.json();
      console.log('✅ Password reset email sent to:', email);
    } catch (error: any) {
      console.error('❌ Password reset error:', error);
      setError(error.message || 'Failed to send password reset email');
      throw error;
    }
  };

  const confirmPasswordReset = async (token: string, newPassword: string): Promise<void> => {
    try {
      setError(null);
      
      // Use backend password reset endpoint
      const response = await fetch(`${getApiUrl()}/auth/reset-password`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ token, newPassword }),
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || 'Failed to reset password');
      }

      const data = await response.json();
      console.log('✅ Password reset confirmed');
    } catch (error: any) {
      console.error('❌ Password reset confirmation error:', error);
      setError(error.message || 'Failed to reset password');
      throw error;
    }
  };

  const clearError = () => {
    setError(null);
  };

  const refreshUser = async (): Promise<void> => {
    if (firebaseUser) {
      try {
        // Force token refresh to get updated user data
        const idToken = await firebaseUser.getIdToken(true);
        const userData = await verifyTokenWithBackend(idToken);
        
        if (userData) {
          // Use Firebase user's displayName if available (it's more up-to-date)
          const finalUserData = {
            ...userData,
            email: userData.email === 'user@example.com' ? firebaseUser.email! : userData.email,
            displayName: firebaseUser.displayName || userData.displayName || null
          };
          setUser(finalUserData);
          console.log('✅ User refreshed:', finalUserData.email);
        }
      } catch (error) {
        console.error('❌ Error refreshing user:', error);
      }
    }
  };

  const value: AuthContextType = {
    user,
    firebaseUser,
    loading,
    error,
    signUp,
    signIn,
    signInWithGoogle,
    signOut,
    sendPasswordReset,
    confirmPasswordReset,
    clearError,
    refreshUser,
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = (): AuthContextType => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export default AuthContext;
